import { ApolloQueryResult } from '@apollo/client';
import { useDisclosure, useToast } from '@chakra-ui/react';
import { useEffect } from 'react';

import { SUCCESS_TOASTS } from '../../../../../constants/toasts';
import {
  ExternalUserPayoffRequestQuery,
  useSubmitAfterHoursSelfServePayoffMutation,
} from '../../../../../gql/generated/graphql';
import { logger } from '../../../../../services/sentry';
import { createErrorToast, createSuccessToast } from '../../../../../utils/toast';
import { PayoffSource, PayoffStepStatus } from '../utils';

type SubmitArgs = {
  vehiclePayoff: number;
  goodThroughDate: Date;
};

type UseSelfServePayoffModalArgs = {
  dealId?: string | null;
  payoffStepStatus?: PayoffStepStatus;
  payoffSource?: PayoffSource;
  refetchPayoff: () => Promise<ApolloQueryResult<ExternalUserPayoffRequestQuery>>;
};

const useSelfServePayoffModal = ({
  dealId,
  payoffStepStatus,
  payoffSource,
  refetchPayoff,
}: UseSelfServePayoffModalArgs) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const [submitSelfServePayoff, { loading: submitting }] =
    useSubmitAfterHoursSelfServePayoffMutation();

  useEffect(() => {
    if (!isOpen) return;
    if (payoffStepStatus !== 'completed') return;
    if (payoffSource === 'self-serve') return;
    onClose();
    toast(createSuccessToast(SUCCESS_TOASTS.payoffFoundAutomatically));
  }, [isOpen, payoffStepStatus, payoffSource, onClose, toast]);

  const handleSubmit = async ({ vehiclePayoff, goodThroughDate }: SubmitArgs) => {
    if (!dealId) return;
    try {
      await submitSelfServePayoff({
        variables: { dealId, vehiclePayoff, goodThroughDate },
      });
      await refetchPayoff();
      onClose();
    } catch (err) {
      logger.error(
        'useSelfServePayoffModal',
        'submitAfterHoursSelfServePayoff failed',
        { dealId },
        err,
      );
      toast(
        createErrorToast({
          errorMessage: err instanceof Error ? err.message : 'Failed to submit payoff',
        }),
      );
    }
  };

  return {
    isOpen,
    open: onOpen,
    close: onClose,
    submit: handleSubmit,
    submitting,
  };
};

export default useSelfServePayoffModal;
